import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  X, Download, FileSpreadsheet, FileText, FileType,
  Check, Layers, ChevronUp, ChevronDown,
} from 'lucide-react';

/**
 * ExportCollectionDialog — universal device-first export.
 *
 * Users pick a format (Excel / CSV / PDF), tune the options, and Zita
 * generates the file on their device. Whatever app they open it with
 * afterwards is their choice.
 *
 * Google Sheets appears here ONLY as an optional convenience destination —
 * never as a required format. If the deployment hasn't enabled it, it just
 * isn't offered. The three standard formats are always live.
 *
 * Props
 *   collection       — { id, name, purpose, item_count }
 *   items            — [{id, ...}]  (already loaded on the parent page)
 *   selectedIds      — Set of item IDs currently selected (may be empty)
 *   onClose()        — dismiss
 */
const FORMAT_META = {
  xlsx: {
    label: 'Excel (.xlsx)',
    icon: FileSpreadsheet,
    description: 'Best for spreadsheets, formatting, and editing.',
  },
  csv: {
    label: 'CSV (.csv)',
    icon: FileText,
    description: 'Best for transferring data into other systems.',
  },
  pdf: {
    label: 'PDF (.pdf)',
    icon: FileType,
    description: 'Best for viewing, printing, and sharing a fixed copy.',
  },
};

// All columns Zita can render. The dialog lets the user pick/reorder any
// subset (or accept the smart default for the collection's purpose).
const ALL_COLUMNS = [
  { key: 'title', label: 'Title' },
  { key: 'price', label: 'Price' },
  { key: 'is_free', label: 'Free?' },
  { key: 'category', label: 'Category' },
  { key: 'brand', label: 'Brand' },
  { key: 'model', label: 'Model' },
  { key: 'condition', label: 'Condition' },
  { key: 'issue', label: 'Issue' },
  { key: 'company', label: 'Company / Employer' },
  { key: 'job_title', label: 'Job title' },
  { key: 'credentials', label: 'Credentials' },
  { key: 'experience', label: 'Experience' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'location_text', label: 'Location' },
  { key: 'distance_miles', label: 'Distance (mi)' },
  { key: 'source_name', label: 'Source' },
  { key: 'external_url', label: 'Original URL' },
  { key: 'search_term', label: 'Search Term' },
  { key: 'scan_id', label: 'Scan ID' },
  { key: 'posted_at', label: 'Posted' },
  { key: 'date_found', label: 'Date Found' },
  { key: 'date_saved', label: 'Date Saved' },
  { key: 'status', label: 'Status' },
  { key: 'tags', label: 'Tags' },
  { key: 'notes', label: 'Notes' },
];

const SORT_OPTIONS = [
  { value: 'date_saved_desc', label: 'Date saved — newest first' },
  { value: 'date_saved_asc', label: 'Date saved — oldest first' },
  { value: 'date_found_desc', label: 'Date found — newest first' },
  { value: 'price_desc', label: 'Price — high to low' },
  { value: 'price_asc', label: 'Price — low to high' },
  { value: 'distance_asc', label: 'Distance — nearest first' },
  { value: 'title_asc', label: 'Title A → Z' },
  { value: 'status_asc', label: 'Status' },
];

// Reasonable default column list per purpose.
const PURPOSE_DEFAULTS = {
  appliances: ['title', 'price', 'brand', 'model', 'condition', 'issue', 'location_text', 'distance_miles', 'source_name', 'external_url', 'search_term', 'date_saved', 'status', 'notes'],
  recruiting: ['title', 'job_title', 'company', 'location_text', 'credentials', 'experience', 'phone', 'email', 'source_name', 'external_url', 'search_term', 'date_saved', 'status', 'notes'],
  business_leads: ['title', 'category', 'location_text', 'phone', 'email', 'company', 'source_name', 'external_url', 'search_term', 'date_saved', 'status', 'notes'],
  jobs: ['title', 'company', 'location_text', 'price', 'category', 'source_name', 'external_url', 'search_term', 'date_saved', 'status', 'notes'],
  '': ['title', 'category', 'price', 'location_text', 'distance_miles', 'source_name', 'external_url', 'search_term', 'date_saved', 'status', 'notes'],
};

export const ExportCollectionDialog = ({
  collection,
  selectedIds,
  onClose,
}) => {
  const [format, setFormat] = useState('xlsx');
  const [sort, setSort] = useState('date_saved_desc');
  const [orientation, setOrientation] = useState('landscape');
  const [includeNotes, setIncludeNotes] = useState(true);
  const [includeSourceLinks, setIncludeSourceLinks] = useState(true);
  const [includeThumbnails, setIncludeThumbnails] = useState(true);
  const [busy, setBusy] = useState(false);
  const [optionalDestinations, setOptionalDestinations] = useState([]);
  const [scope, setScope] = useState('all'); // 'all' | 'selected'
  const [showColumns, setShowColumns] = useState(false);

  const defaults = PURPOSE_DEFAULTS[collection?.purpose || ''] || PURPOSE_DEFAULTS[''];
  const [columns, setColumns] = useState(defaults);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/collections/exports/formats');
        setOptionalDestinations(data.optional_destinations || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const hasSelection = (selectedIds?.size || 0) > 0;
  useEffect(() => { setScope(hasSelection ? 'selected' : 'all'); }, [hasSelection]);

  const columnsAvailable = useMemo(() => {
    const chosen = new Set(columns);
    return ALL_COLUMNS.filter((c) => !chosen.has(c.key));
  }, [columns]);

  const toggleCol = (key) => {
    setColumns((cs) => cs.includes(key) ? cs.filter((k) => k !== key) : [...cs, key]);
  };
  const moveCol = (idx, dir) => {
    setColumns((cs) => {
      const copy = [...cs];
      const to = idx + dir;
      if (to < 0 || to >= copy.length) return copy;
      const [item] = copy.splice(idx, 1);
      copy.splice(to, 0, item);
      return copy;
    });
  };

  const performExport = async () => {
    if (!collection) return;
    setBusy(true);
    try {
      const payload = {
        format,
        columns,
        sort,
        orientation,
        include_notes: includeNotes,
        include_source_links: includeSourceLinks,
        include_thumbnails: includeThumbnails && format === 'pdf',
        selected_ids: scope === 'selected' ? [...(selectedIds || new Set())] : [],
      };
      const res = await api.post(`/collections/${collection.id}/export`, payload, {
        responseType: 'blob',
      });
      const meta = FORMAT_META[format];
      const ext = format === 'xlsx' ? 'xlsx' : format;
      const mime = format === 'csv'
        ? 'text/csv'
        : (format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(collection.name || 'collection').replace(/\s+/g, '_')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${meta.label} downloaded.`);
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed.');
    } finally {
      setBusy(false);
    }
  };

  const sendToGoogleSheets = async (dest) => {
    if (!dest.connected) {
      try {
        const { data } = await api.get('/collections/google/oauth/start');
        window.location.href = data.auth_url;
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not start Google authentication.');
      }
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/collections/${collection.id}/export/google-sheets`);
      toast.success('Google Sheet created.');
      window.open(data.sheet_url, '_blank', 'noopener,noreferrer');
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send to Google Sheets.');
    } finally { setBusy(false); }
  };

  if (!collection) return null;

  const Icon = FORMAT_META[format].icon;

  return (
    <div
      role="dialog"
      aria-label="Export collection"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      data-testid="export-dialog"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-2xl zita-card p-6 sm:p-7 zita-fade-up max-h-[92vh] overflow-y-auto zita-scroll">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-[hsl(var(--zita-silver-soft))] zita-ink-muted"
        >
          <X size={18} />
        </button>

        <div className="text-xs uppercase tracking-[0.16em] zita-ink-muted flex items-center gap-1.5">
          <Layers size={12} /> Export Collection
        </div>
        <h2 className="font-serif text-2xl zita-ink mt-1 truncate">{collection.name}</h2>
        <p className="text-xs zita-ink-muted mt-0.5">
          Zita generates the file on your device. Open it with any compatible
          app — Excel, Numbers, Sheets, LibreOffice, Acrobat, email, cloud
          storage, or anything else you already use.
        </p>

        {/* Format picker */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="export-format-picker">
          {['xlsx', 'csv', 'pdf'].map((k) => {
            const m = FORMAT_META[k];
            const M = m.icon;
            const active = format === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setFormat(k)}
                data-testid={`export-format-${k}`}
                className={`text-left p-3 rounded-xl border transition ${
                  active
                    ? 'border-[color:var(--zita-blue)] bg-[color:var(--zita-blue-soft)]/50 zita-ink'
                    : 'border-[color:var(--zita-hairline)] hover:border-[color:var(--zita-blue-soft)] zita-ink-soft'
                }`}
              >
                <M size={18} className={active ? 'zita-blue-text' : 'zita-ink-muted'} />
                <div className="mt-1.5 text-sm font-semibold">{m.label}</div>
                <div className="text-[11px] zita-ink-muted mt-0.5 leading-snug">{m.description}</div>
              </button>
            );
          })}
        </div>

        {/* Scope */}
        <div className="mt-5">
          <label className="text-[10px] uppercase tracking-[0.14em] zita-ink-muted">Scope</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <button
              type="button"
              onClick={() => setScope('all')}
              data-testid="export-scope-all"
              className={`p-2 text-xs rounded-lg border ${scope === 'all' ? 'border-[color:var(--zita-blue)] bg-[color:var(--zita-blue-soft)]/40 zita-ink' : 'border-[color:var(--zita-hairline)] zita-ink-soft'}`}
            >
              Export entire collection ({collection.item_count} item{collection.item_count === 1 ? '' : 's'})
            </button>
            <button
              type="button"
              onClick={() => setScope('selected')}
              disabled={!hasSelection}
              data-testid="export-scope-selected"
              className={`p-2 text-xs rounded-lg border disabled:opacity-40 ${scope === 'selected' ? 'border-[color:var(--zita-blue)] bg-[color:var(--zita-blue-soft)]/40 zita-ink' : 'border-[color:var(--zita-hairline)] zita-ink-soft'}`}
            >
              Export selected ({selectedIds?.size || 0})
            </button>
          </div>
        </div>

        {/* Sort */}
        <div className="mt-4">
          <label className="text-[10px] uppercase tracking-[0.14em] zita-ink-muted">Sort order</label>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="export-sort"
            className="zita-input w-full px-3 py-2 text-sm mt-1"
          >
            {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {/* PDF-only orientation */}
        {format === 'pdf' && (
          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-[0.14em] zita-ink-muted">Orientation</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {['landscape', 'portrait'].map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setOrientation(o)}
                  data-testid={`export-orientation-${o}`}
                  className={`p-2 text-xs rounded-lg border capitalize ${orientation === o ? 'border-[color:var(--zita-blue)] bg-[color:var(--zita-blue-soft)]/40 zita-ink' : 'border-[color:var(--zita-hairline)] zita-ink-soft'}`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Toggles */}
        <div className="mt-4 grid sm:grid-cols-3 gap-2">
          <Toggle
            label="Include notes"
            checked={includeNotes}
            onChange={setIncludeNotes}
            testid="export-toggle-notes"
          />
          <Toggle
            label="Include source links"
            checked={includeSourceLinks}
            onChange={setIncludeSourceLinks}
            testid="export-toggle-sources"
          />
          {format === 'pdf' && (
            <Toggle
              label="Thumbnails (PDF)"
              checked={includeThumbnails}
              onChange={setIncludeThumbnails}
              testid="export-toggle-thumbs"
            />
          )}
        </div>

        {/* Column customization */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowColumns((v) => !v)}
            data-testid="export-columns-toggle"
            className="text-xs zita-ink-soft hover:zita-ink underline"
          >
            {showColumns ? 'Hide' : 'Customize'} columns ({columns.length} selected)
          </button>
          {showColumns && (
            <div className="mt-2 space-y-2 max-h-64 overflow-y-auto zita-scroll pr-1 border border-[color:var(--zita-hairline)] rounded-lg p-2" data-testid="export-columns-panel">
              <div className="text-[10px] uppercase tracking-[0.14em] zita-ink-muted">Selected (drag with arrows to reorder)</div>
              {columns.map((k, idx) => {
                const meta = ALL_COLUMNS.find((c) => c.key === k);
                if (!meta) return null;
                return (
                  <div key={k} className="flex items-center gap-2 text-xs">
                    <span className="w-5 zita-ink-muted">{idx + 1}.</span>
                    <span className="flex-1 zita-ink-soft">{meta.label}</span>
                    <button type="button" onClick={() => moveCol(idx, -1)} className="p-1 hover:bg-[hsl(var(--zita-silver-soft))] rounded" aria-label="Move up"><ChevronUp size={12} /></button>
                    <button type="button" onClick={() => moveCol(idx, 1)} className="p-1 hover:bg-[hsl(var(--zita-silver-soft))] rounded" aria-label="Move down"><ChevronDown size={12} /></button>
                    <button type="button" onClick={() => toggleCol(k)} className="text-red-500 hover:text-red-700 text-xs" data-testid={`export-col-remove-${k}`}>Remove</button>
                  </div>
                );
              })}
              {columnsAvailable.length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-[0.14em] zita-ink-muted mt-3">Available to add</div>
                  <div className="flex flex-wrap gap-1">
                    {columnsAvailable.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => toggleCol(c.key)}
                        data-testid={`export-col-add-${c.key}`}
                        className="text-[11px] px-2 py-0.5 rounded-full border border-dashed border-[color:var(--zita-blue-soft)] zita-ink-soft hover:bg-[color:var(--zita-blue-soft)]/40"
                      >
                        + {c.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <div className="text-[10px] zita-ink-muted italic mt-3">
                Tip: click a column to remove it, or use ↑/↓ to reorder.
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap items-center gap-2 justify-end">
          <button type="button" onClick={onClose} className="zita-btn-secondary text-sm">Cancel</button>
          <button
            type="button"
            onClick={performExport}
            disabled={busy}
            data-testid="export-run"
            className="zita-btn-primary text-sm inline-flex items-center gap-1"
          >
            {busy ? 'Generating…' : (<><Download size={13} /> <Icon size={13} /> {scope === 'selected' ? 'Export selected' : 'Export entire collection'}</>)}
          </button>
        </div>

        {/* Optional convenience destinations (only if the deployment enabled them) */}
        {optionalDestinations.length > 0 && (
          <div className="mt-6 pt-4 border-t border-[color:var(--zita-hairline)]">
            <div className="text-[10px] uppercase tracking-[0.14em] zita-ink-muted mb-2">
              Optional — send directly to a connected app
            </div>
            <div className="space-y-2">
              {optionalDestinations.map((dest) => (
                <button
                  key={dest.key}
                  type="button"
                  data-testid={`export-optional-${dest.key}`}
                  onClick={() => sendToGoogleSheets(dest)}
                  disabled={busy}
                  className="w-full text-left px-3 py-2 rounded-lg border border-[color:var(--zita-hairline)] hover:border-[color:var(--zita-blue-soft)] text-sm"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium zita-ink">{dest.label}</div>
                      <div className="text-[11px] zita-ink-muted">{dest.description}</div>
                    </div>
                    <div className="text-[10px] zita-ink-muted">
                      {dest.connected ? <Check size={12} className="zita-blue-text" /> : 'Connect'}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Toggle = ({ label, checked, onChange, testid }) => (
  <label
    className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs ${checked ? 'border-[color:var(--zita-blue)] bg-[color:var(--zita-blue-soft)]/30 zita-ink' : 'border-[color:var(--zita-hairline)] zita-ink-soft'}`}
    data-testid={testid}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="rounded"
    />
    <span>{label}</span>
  </label>
);

export default ExportCollectionDialog;
